import type { AxiosError } from 'axios';
import axiosInstance from './interceptor.services';

export type TReconciliationProcessor =
  'mock' | 'market_pay' | 'on_site' | 'payfast';

export type TReconciliationFindingKind =
  | 'MISSING_SETTLEMENT'
  | 'UNRECORDED_REFUND'
  | 'STALE_SETTLEMENT'
  | 'ORPHAN_TRANSACTION';

export type TReconciliationOutcome = 'REPAIRED' | 'BLOCKED';

export type TReconciliationBlockedReason =
  | 'ORDER_ENGAGED'
  | 'UNPAY_NOT_AUTOMATED'
  | 'AMBIGUOUS_MATCH'
  | 'NO_LOCAL_ORDER'
  | 'NO_PENDING_ROW'
  | 'REFUND_ORIGIN_UNKNOWN'
  | 'AUTO_REPAIR_DISABLED';

export type TReconciliationFindingSortBy = 'createdAt' | 'updatedAt' | 'amount';

export interface IReconciliationFinding {
  id: string;
  createdAt: string;
  updatedAt: string;
  storeId: string;
  processor: TReconciliationProcessor;
  providerTransactionId: string;
  kind: TReconciliationFindingKind;
  outcome: TReconciliationOutcome;
  blockedReason: TReconciliationBlockedReason | null;
  reference: string | null;
  orderId: string | null;
  paymentId: string | null;
  providerStatus: string;
  amount: number;
  currency: string;
  payload: Record<string, unknown>;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
}

export interface IReconciliationSweepResult {
  storesSwept: number;
  transactionsSeen: number;
  repaired: number;
  blocked: number;
  pageCapReached: string[];
  unresolvedFindings: number;
  sweptAt: string;
}

export interface ISearchReconciliationFindingsParams {
  storeId?: string;
  processor?: TReconciliationProcessor[];
  kind?: TReconciliationFindingKind[];
  outcome?: TReconciliationOutcome[];
  reference?: string;
  unresolvedOnly?: boolean;
  sortBy?: TReconciliationFindingSortBy;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
  pagination?: boolean;
}

// Le pipe de validation NestJS whitelist strictement les clés du DTO : `processor[]`
// (sérialisation tableau par défaut d'axios) n'existe pas dessus, seul `processor`
// répété existe — même contrat que PaymentService.buildParams.
function buildFindingsParams(
  params: ISearchReconciliationFindingsParams,
): URLSearchParams {
  const qs = new URLSearchParams();
  qs.append('pagination', 'true');
  qs.append('page', String(params.page ?? 1));
  qs.append('pageSize', String(params.pageSize ?? 20));
  qs.append('sortBy', params.sortBy ?? 'createdAt');
  qs.append('sortOrder', params.sortOrder ?? 'DESC');
  for (const v of params.processor ?? []) qs.append('processor', v);
  for (const v of params.kind ?? []) qs.append('kind', v);
  for (const v of params.outcome ?? []) qs.append('outcome', v);
  if (params.storeId) qs.append('storeId', params.storeId);
  if (params.reference) qs.append('reference', params.reference);
  if (params.unresolvedOnly !== undefined)
    qs.append('unresolvedOnly', String(params.unresolvedOnly));
  return qs;
}

export const PaymentReconciliationService = {
  runSweep: async (storeId?: string) => {
    try {
      const response = await axiosInstance.post<{
        data: IReconciliationSweepResult;
        message: string;
      }>('payment-reconciliation/sweep', storeId ? { storeId } : {});
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  searchFindings: async (params: ISearchReconciliationFindingsParams) => {
    try {
      const qs = buildFindingsParams(params);
      const response = await axiosInstance.get<{
        data: IReconciliationFinding[];
        message: string;
        total: number;
      }>(`payment-reconciliation/findings?${qs.toString()}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  applyFinding: async (findingId: string) => {
    try {
      const response = await axiosInstance.post<{
        data: IReconciliationFinding;
        message: string;
      }>(`payment-reconciliation/findings/${findingId}/apply`, {});
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  resolveFinding: async (findingId: string) => {
    try {
      const response = await axiosInstance.post<{
        data: IReconciliationFinding;
        message: string;
      }>(`payment-reconciliation/findings/${findingId}/resolve`, {});
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
